import { z } from "zod";
import { ConsentScopeSchema } from "./consent.js";
import { ActivationPredicateSchema } from "./personaActivation.js";
/**
 * PersonaContext: the static + slowly-changing lens HealthOS uses to
 * interpret Signals. See docs/HealthOS_Product_Plan.md section 5 and
 * docs/HealthOS_Design_Doc.md section 4.
 *
 * `personaId` points at a persona config in `packages/persona-configs`
 * (e.g. `glp1_companion.json`). The companion-web ambient screen and TAM
 * prompts both hydrate themselves from this shape.
 */
/**
 * Persona ids are slugs, not a closed enum. Validity is "is this slug
 * registered in @healthos/persona-configs?", checked by the registry at
 * use-time. The schema only enforces shape: lowercase ASCII, digits,
 * underscores, starts with a letter, length <= 64.
 *
 * This shift mirrors the outcome-strategy pattern in `outcomeStrategy.ts`
 * (metric: z.string()) and removes the schema-level lock-in that made
 * adding a persona a multi-package change. Consumers that previously
 * relied on closed-enum exhaustiveness (TS switch statements,
 * Record<PersonaId, X>) still type-check; the looseness is the point.
 */
export const PersonaIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, "persona id must be lowercase ASCII, digits, or underscores; start with a letter");
export const PersonaContextSchema = z.object({
    userId: z.string().min(1),
    /**
     * Personas active for this user, in priority order (highest first).
     * Multi-persona by design: a user can simultaneously be "GLP-1 +
     * cardiometabolic + sleep recovery", and the engine reads their
     * merged manifest view (see `mergePersonas()`). The first entry is
     * the "primary" lens used wherever a single persona must be picked
     * for UX (e.g. the homepage hero copy).
     *
     * Single-persona before; widened here in Sprint E. The runtime layer
     * persists this as `onboarding_profiles.data.personaIds` (JSONB,
     * additive, no DB migration). Existing rows that carry the old
     * singular `persona` field are read with a tiny adapter that wraps
     * it into a one-element array - kept until the next onboarding
     * write replaces it cleanly.
     */
    personaIds: z.array(PersonaIdSchema).min(1),
    /** Operator-set forced persona pin. When present, overrides predicate
     *  matching in `resolvePersonas()`. The Demo Control Panel writes here
     *  to force a persona for live demos regardless of signal state. */
    personaPinOverride: z.array(PersonaIdSchema).default([]),
    startedAt: z.string().datetime(),
    phase: z
        .enum(["onboarding", "early", "steady", "transition", "maintenance"])
        .default("onboarding"),
    goals: z.array(z.string().max(200)).max(10).default([]),
    conditions: z.array(z.string().max(120)).max(20).default([]),
    medications: z
        .array(z.object({
        name: z.string().max(120),
        class: z.string().max(60).optional(),
        startedAt: z.string().datetime().optional(),
    }))
        .max(40)
        .default([]),
    clinicalMarkers: z
        .object({
        a1c: z.number().min(0).max(20).optional(),
        a1cTarget: z.number().min(0).max(20).optional(),
        weightKg: z.number().min(0).max(500).optional(),
        weightTargetKg: z.number().min(0).max(500).optional(),
    })
        .partial()
        .default({}),
    cadencePrefs: z
        .object({
        quietHours: z
            .object({
            start: z.string().regex(/^\d{2}:\d{2}$/),
            end: z.string().regex(/^\d{2}:\d{2}$/),
            timezone: z.string(),
        })
            .optional(),
        maxInterruptionsPerDay: z.number().int().min(0).max(12).default(3),
        preferredSurfaces: z
            .array(z.enum(["companion-web", "companion-mobile", "email", "push", "sms", "partner-embed"]))
            .default(["companion-web"]),
    })
        /*
         * Zod 4 enforces that a top-level `.default()` on an object schema
         * provides every required field; field-level defaults inside no
         * longer apply when the parent is missing entirely.
         */
        .default({ maxInterruptionsPerDay: 3, preferredSurfaces: ["companion-web"] }),
    locale: z.string().min(2).max(36).default("en-US"),
});
// ---------------------------------------------------------------------------
// PersonaManifest - the static config that declares what a persona DOES.
// Distinct from PersonaContext (per-user runtime state). Manifests live in
// `@healthos/persona-configs`; this schema is the contract.
// ---------------------------------------------------------------------------
/**
 * What outcome HealthOS measures for this persona, and at what threshold the
 * cost curve bends. For GLP-1 this is adherence_rate at 0.80 (the Aon
 * threshold). Outcome bar is what matched-cohort exports group by.
 */
export const OutcomeBarSchema = z.object({
    /** Stable id ('adherence_rate', 'weight_loss_pct', 'hba1c_delta', …). */
    metric: z.string().min(1).max(80),
    /** Target value at which the persona is considered 'on track'. */
    target: z.number(),
    /** Direction the metric must move to count as progress. */
    direction: z.enum(["gte", "lte", "eq"]),
    /** Outcome window over which the metric is computed. */
    windowDays: z.number().int().min(1).max(3650),
    /** Human-readable label for surfaces (e.g. "Adherence ≥ 80% over 12 months"). */
    label: z.string().min(1).max(160),
});
/**
 * Per-surface delivery posture. Defines which output kinds the persona is
 * allowed to emit on this surface, and the daily cap before the AttentionBudget
 * starts dropping events. Kept persona-by-surface so the SDLC story is
 * "config-only" rather than touching engine code.
 */
export const SurfacePolicySchema = z.object({
    surface: z.enum(["companion-web", "companion-mobile", "email", "push", "sms", "partner-embed"]),
    enabled: z.boolean().default(true),
    /** Output kinds (ActionPlan.kind) this persona is allowed to ship here. */
    allowedKinds: z
        .array(z.enum(["plan", "recommendation", "reflection", "connect", "escalate", "quiet", "artifact"]))
        .min(1),
    /** Hard daily cap; AttentionBudget rejects beyond this. */
    dailyCap: z.number().int().min(0).max(24).default(3),
});
/**
 * What HealthOS is allowed to claim attribution for, and the cohort export
 * groups. The measurement thesis - these are the lines a CFO grep's against
 * a claims report.
 */
export const AttributionTargetSchema = z.object({
    /** Stable id used in cohort exports ('cv_event', 'weight_regain_18mo', 'pharmacy_pmpm'). */
    id: z.string().min(1).max(80),
    label: z.string().min(1).max(160),
    /** ICD/CPT/Rx code anchors that map this back to claims data, when applicable. */
    codeRefs: z.array(z.string().min(1).max(40)).max(40).default([]),
});
/**
 * AmbientCopy - the per-persona view-model for the patient `/today` surface.
 *
 * Pure JSON, no JSX. Icons are passed by stable key (resolved to a
 * Phosphor component on the client); headline emphasis is split into
 * a lead string + emphasis string so the renderer can italicise the
 * contextual phrase without the manifest carrying React.
 *
 * Personas that don't surface to /today can omit this block; the page
 * falls back to a quiet placeholder.
 */
export const EnvIconKeySchema = z.enum([
    "thermometer",
    "drop",
    "coffee",
    "fork",
    "moon",
    "bed",
    "heart",
    "wind",
    "sun",
]);
export const AmbientCopySchema = z.object({
    /** Short label for the date/time crumb, e.g. "Sleep Companion". */
    surfaceLabel: z.string().min(1).max(80),
    greetings: z.object({
        morning: z.object({
            title: z.string().min(1).max(160),
            body: z.string().min(1).max(400),
        }),
        evening: z.object({
            title: z.string().min(1).max(160),
            body: z.string().min(1).max(400),
        }),
    }),
    outcome: z.object({
        eyebrow: z.string().min(1).max(80),
        stat: z.string().min(1).max(20),
        statSuffix: z.string().max(20).optional(),
        unit: z.string().min(1).max(80),
        delta: z.string().max(80).optional(),
        points: z.array(z.number()).min(1).max(14),
        threshold: z.number(),
        thresholdSuffix: z.string().max(20).optional(),
        rangeLabel: z.string().max(40).optional(),
        footLabel: z.string().min(1).max(40),
        footValue: z.string().min(1).max(160),
    }),
    environment: z.object({
        eyebrow: z.string().min(1).max(40),
        /** Lead sentence rendered as plain text. */
        headlineLead: z.string().min(1).max(240),
        /** Italicised contextual phrase appended to the lead. */
        headlineEmphasis: z.string().min(1).max(160),
        rows: z
            .array(z.object({
            iconKey: EnvIconKeySchema,
            label: z.string().min(1).max(80),
            sub: z.string().min(1).max(240),
            value: z.string().min(1).max(40),
            pill: z.string().max(40).optional(),
            level: z.enum(["good", "watch", "alert"]),
        }))
            .min(1)
            .max(6),
        now: z.number().min(0).max(100),
        window: z.object({ start: z.number(), end: z.number() }).optional(),
        marks: z
            .array(z.object({ at: z.number().min(0).max(100), label: z.string().min(1).max(40) }))
            .max(6),
        footLeft: z.string().min(1).max(120),
        footRight: z.string().min(1).max(160),
    }),
    attention: z.object({
        eyebrow: z.string().min(1).max(80),
        headline: z.string().min(1).max(240),
        whyTag: z.string().min(1).max(40),
        why: z.string().min(1).max(480),
        watching: z
            .array(z.object({ label: z.string().min(1).max(80), value: z.string().min(1).max(80) }))
            .max(6),
        primaryAction: z.string().min(1).max(40),
        ghostAction: z.string().min(1).max(40),
        meta: z.string().max(160).optional(),
        /**
         * Inline provenance for the attention
         * card's main claim (e.g. "halves nausea over 4 hours").
         * The persona-static attention object is the path that actually
         * renders for users on a derived persona without a real microstep
         * - only instrumented microstepToAttention, missing this
         * code path. Optional - cards without a methodology block render
         * unchanged.
         */
        methodology: z
            .object({
            title: z.string().min(1).max(120),
            body: z.string().min(1).max(800),
            source: z.string().min(1).max(240).optional(),
        })
            .optional(),
    }),
    summary: z.object({
        /** Big numeric, e.g. "7.0" / "86" / "−4". */
        stat: z.string().min(1).max(20),
        /** Small unit appended to the stat, e.g. "h" / "%" / " mmHg". */
        statUnit: z.string().max(20).optional(),
        statLabel: z.string().min(1).max(80),
        rangeLabel: z.string().min(1).max(40),
        tagLabel: z.string().min(1).max(40),
        headline: z.string().min(1).max(160),
        text: z.string().min(1).max(400),
    }),
    plan: z.object({
        headline: z.string().min(1).max(120),
        text: z.string().min(1).max(400),
    }),
    /** Optional Brief host override. When set, the Weekly Brief card uses
     *  this host (name, role, photo) instead of the API's default. Each
     *  persona can carry its own narrator (e.g. Arianna for sleep,
     *  Joey for general). */
    brief: z
        .object({
        host: z.object({
            name: z.string().min(1).max(80),
            role: z.string().max(80).optional(),
            organization: z.string().max(80).optional(),
            tagline: z.string().max(120).optional(),
            photoUrl: z.string().min(1).max(280),
        }),
    })
        .optional(),
});
export const PersonaManifestSchema = z.object({
    personaId: PersonaIdSchema,
    displayName: z.string().min(1).max(120),
    /** SemVer for the manifest. Bump on any field change so downstream caches
     *  invalidate cleanly and we can audit which version produced an outcome. */
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    /** Disabled manifests can ship in the package but aren't returned to
     *  end-users. Lets us stage a persona before launch. */
    enabled: z.boolean().default(false),
    /** Onboarding copy the SPA can read for the focus/story prompts. */
    onboardingCopy: z.object({
        eyebrow: z.string().max(80),
        headline: z.string().max(160),
        blurb: z.string().max(400),
    }),
    /** Patient `/today` view-model. Optional, personas that don't surface
     *  to the ambient companion can omit it. */
    ambientCopy: AmbientCopySchema.optional(),
    /**
     * Patient `/plan` view-model. Persona-specific copy + asset slugs for
     * the plan screen's five regions. Optional - personas that don't
     * surface to /plan (operator-internal personas, future demos) can omit
     * it; the engine falls back to the generic manifest. ADR-0008: this
     * keeps persona content in config, so the engine never branches on
     * personaId.
     */
    planScreen: z
        .object({
        /** "Sleep plan", "GLP-1 plan", etc. Region 1 eyebrow. */
        eyebrow: z.string().min(1).max(60),
        /** Three-part italic-accent headline rendered on Region 1. */
        headline: z.object({
            lead: z.string().min(1).max(80),
            accent: z.string().min(1).max(60),
            tail: z.string().max(20),
        }),
        /**
         * Region 1's "What's working hardest" tile. The single biggest
         * lever for this persona, in editorial prose.
         */
        lever: z.object({
            name: z.string().min(1).max(80),
            foot: z.string().min(1).max(240),
        }),
        /** Region 1 ProgressTile label - "Nights with 7+ hours of sleep". */
        outcomeLabel: z.string().min(1).max(80),
        /** Region 1 rationale prose for users 21+ days into the plan. */
        deepRationale: z.string().min(1).max(400),
        /**
         * Region 4 themed groups. Each entry is one collapsible section
         * with a title + sub-line; items get filtered into a section by
         * matching `healthBehaviours` against the section's tag list.
         */
        behaviours: z
            .array(z.object({
            name: z.string().min(1).max(60),
            sub: z.string().min(1).max(120),
            /** Lower-case tags matched against `microsteps.healthBehaviours`. */
            match: z.array(z.string().min(1).max(60)).min(1).max(8),
        }))
            .min(1)
            .max(8),
        /** Footer "Reviewed by..." line. */
        reviewer: z.string().min(1).max(160),
        /**
         * Slug into the SPA's bundled photo set; resolves to
         * `/plan-images/{moodImage}.jpg`. Drives the Region 1 mood
         * tile and the per-card fallback when an item has no own
         * mediaKey.
         */
        moodImage: z.string().min(1).max(40),
        /**
         * Phase-aware editorial copy for the page's spine. The previous
         * single `headline` field shipped the same italic-accented sentence
         * to every user on the persona regardless of state (cycle 1 day 1
         * vs cycle 5 day 6 read identically). This field set carries
         * cycle-state-aware variants, picked by the route from
         * `(cycleNumber, cyclePhase)`. Each variant is a {before, emph,
         * after} triple matching the SPA's <em>-accented render.
         */
        narrative: z
            .object({
            /** Cycle 1 (day 1-7), active phase. The "we're listening" beat. */
            fresh: z.object({
                headline: z.object({
                    before: z.string().min(1).max(80),
                    emph: z.string().min(1).max(60),
                    after: z.string().min(1).max(80),
                }),
                sub: z.string().min(1).max(400),
            }),
            /** Cycle 2+ active. The "you're doing X more steadily" beat. */
            inFlight: z.object({
                headline: z.object({
                    before: z.string().min(1).max(80),
                    emph: z.string().min(1).max(60),
                    after: z.string().min(1).max(80),
                }),
                sub: z.string().min(1).max(400),
            }),
            /** Reflection day, any cycle. */
            reflection: z.object({
                headline: z.object({
                    before: z.string().min(1).max(80),
                    emph: z.string().min(1).max(60),
                    after: z.string().min(1).max(80),
                }),
                sub: z.string().min(1).max(400),
            }),
        })
            .optional(),
        /**
         * Three-card stat row that sits below the cycle anchor. The
         * page's argument: "here's what we're measuring + here's the
         * lever." Two metric cards (eyebrow + value-bar-foot template
         * the route fills with real numbers) plus one highlight card
         * (eyebrow + headline + body). The metric cards have a
         * `signalKind` the route reads to compute the value; the
         * highlight card is the persona's `lever` recast.
         */
        statTemplate: z
            .object({
            metric1: z.object({
                eyebrow: z.string().min(1).max(80),
                valueTail: z.string().min(1).max(40),
                /** Signal kind to query (e.g. "wearable.sleep" for hours-slept rollup). */
                signalKind: z.string().min(1).max(60),
                /** What to render when no signals exist yet. */
                freshFoot: z.string().min(1).max(160),
                /** What to render when signals exist (templated; "{value}" placeholder). */
                inFlightFoot: z.string().min(1).max(160),
                /** Bar style: "bar" or "dots:N". */
                bar: z.string().min(1).max(20),
            }),
            metric2: z.object({
                eyebrow: z.string().min(1).max(80),
                valueTail: z.string().min(1).max(40),
                signalKind: z.string().min(1).max(60),
                freshFoot: z.string().min(1).max(160),
                inFlightFoot: z.string().min(1).max(160),
                bar: z.string().min(1).max(20),
            }),
            highlight: z.object({
                inFlightEyebrow: z.string().min(1).max(80),
                freshEyebrow: z.string().min(1).max(80),
            }),
        })
            .optional(),
    })
        .optional(),
    /** What outcome moves and at what threshold it 'works'. */
    outcomeBar: OutcomeBarSchema,
    /** Optional secondary outcomes - same shape, used for cohort sub-analyses. */
    attributionTargets: z.array(AttributionTargetSchema).max(20).default([]),
    /** Consent scopes the persona requires to function. Onboarding must collect
     *  these before TAM is callable. */
    requiredConsentScopes: z.array(ConsentScopeSchema).min(1),
    /** Hints prepended to the TAM prompt for this persona; bounded to keep
     *  the prompt small. */
    tamPromptHints: z.array(z.string().min(1).max(500)).max(8).default([]),
    /** Per-surface policy. */
    surfacePolicies: z.array(SurfacePolicySchema).min(1),
    /** Default quiet hours; user can override in settings. */
    quietHoursDefault: z.object({
        start: z.string().regex(/^\d{2}:\d{2}$/),
        end: z.string().regex(/^\d{2}:\d{2}$/),
    }),
    /**
     * Activation predicate. When present, `resolvePersonas()` evaluates
     * this against the user's current context (signals, conditions,
     * medications, clinical markers) to decide whether the persona
     * applies. When absent, the persona is opt-in only - it can be
     * pinned via `personaPinOverride` but never auto-activates from
     * signal evidence. Existing manifests left their `activation`
     * unset preserve current pick-at-onboarding behaviour.
     */
    activation: ActivationPredicateSchema.optional(),
    /**
     * Tie-breaker on overlap. Higher priority wins when two personas
     * disagree about a per-surface policy (see merge semantics in the
     * resolver). Defaults to 0; absolute value carries no meaning, only
     * relative ordering. Conventional ranges: 0 = baseline, 10 =
     * domain-specific (sleep, GLP-1), 20 = clinical (cardiometabolic),
     * 30 = crisis-adjacent (cancer caregiver).
     */
    priority: z.number().int().default(0),
});
/**
 * Public-facing slice of a manifest - what /api/personas exposes to the SPA.
 * Strips engine-only fields (tamPromptHints) that the client doesn't need
 * and shouldn't see.
 */
export const PublicPersonaManifestSchema = PersonaManifestSchema.pick({
    personaId: true,
    displayName: true,
    version: true,
    enabled: true,
    onboardingCopy: true,
    outcomeBar: true,
    requiredConsentScopes: true,
    quietHoursDefault: true,
}).extend({
    surfaces: z.array(z.string()),
});
export function toPublicManifest(m) {
    return PublicPersonaManifestSchema.parse({
        personaId: m.personaId,
        displayName: m.displayName,
        version: m.version,
        enabled: m.enabled,
        onboardingCopy: m.onboardingCopy,
        outcomeBar: m.outcomeBar,
        requiredConsentScopes: m.requiredConsentScopes,
        quietHoursDefault: m.quietHoursDefault,
        surfaces: m.surfacePolicies.filter((p) => p.enabled).map((p) => p.surface),
    });
}
//# sourceMappingURL=persona.js.map